<script setup lang="ts">
import { useUser } from '@/services/user';
import { useBreakpoints } from '@/utils/breakpoints';
import { useUserNotifications } from '@/utils/useUserNotifications';
import { faCircleNotch, faMailbox } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@teemill/components';
import type { Notification as UserNotification } from '@teemill/dashboard-user';
import { ShoveNotification, useNotificationsStore } from '@teemill/modules/notifications';
import { useInfiniteScroll } from '@vueuse/core';
import { computed, ref, useTemplateRef, watch } from 'vue';

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const props = defineProps<{
  search?: string;
}>();

const breakpoints = useBreakpoints();
const greaterOrEqualToSm = breakpoints.greaterOrEqual('sm');
const notificationsScrollRef = useTemplateRef('notificationsScrollRef');
const { decrementUnreadCount } = useNotificationsStore();
const user = useUser();
const search = computed(() => props.search);
const sortBy = ref<string[] | undefined>(undefined);

const {
  notifications,
  loadMore: loadMoreNotifications,
  hasMore,
  isLoading,
  reset: resetNotifications,
} = useUserNotifications({ search, sortBy });

const deleteNotification = async (notification: UserNotification) => {
  notifications.value = notifications.value.filter((n) => n.id !== notification.id);
  if (!notification.readAt) {
    decrementUnreadCount();
  }

  try {
    await user.notifications.delete(notification.id);
  } catch {
    // Silently fail on delete errors
  }
};

const readNotification = async (notification: UserNotification) => {
  if (notification.readAt) {
    return;
  }

  notification.readAt = new Date().toISOString();
  if (!notification.readAt) {
    decrementUnreadCount();
  }

  try {
    await user.notifications.read(notification.id);
  } catch {
    // Silently fail on read errors
  }
};

const clickHandler = (notification: UserNotification, preventClose: boolean = false) => {
  if (!preventClose) {
    emit('close');
  }

  readNotification(notification);
};

useInfiniteScroll(
  notificationsScrollRef,
  async () => {
    if (hasMore.value && !isLoading.value) {
      await loadMoreNotifications();
    }
  },
  { distance: 500, canLoadMore: () => hasMore.value },
);

watch(search, () => resetNotifications(), { immediate: true });
</script>

<template>
  <div
    ref="notificationsScrollRef"
    class="flex flex-col overflow-y-auto overflow-x-hidden min-h-[217px] sm:max-h-[65vh]"
    :class="{ 'h-[60vh]': !greaterOrEqualToSm }"
  >
    <template v-if="notifications.length > 0">
      <template v-for="(notification, index) in notifications" :key="notification.id">
        <ShoveNotification
          :notification="notification"
          @remove="deleteNotification(notification)"
          @click="(preventClose: boolean) => clickHandler(notification, preventClose)"
        />
        <hr v-if="index !== notifications.length - 1" />
      </template>
    </template>
    <div v-else class="text-center m-auto">
      <template v-if="isLoading">
        <FontAwesomeIcon :icon="faCircleNotch" spin class="text-4xl mb-3" />
        <p>Loading notifications...</p>
      </template>
      <template v-else>
        <FontAwesomeIcon :icon="faMailbox" class="text-4xl mb-3" />
        <p>You're all caught up</p>
      </template>
    </div>
  </div>
</template>
